// src/context/AuthContext.js

import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getFirebaseToken } from "../authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Esperar a que Firebase restaure la sesión desde el almacenamiento persistente
    // onAuthStateChanged se ejecuta automáticamente cuando Firebase detecta un cambio
    // en el estado de autenticación, incluyendo cuando restaura la sesión al recargar
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Asegurar que el token esté actualizado en localStorage
        try {
          const token = await getFirebaseToken();
          if (token) {
            localStorage.setItem("token", token);
            localStorage.setItem("authToken", token);
            localStorage.setItem("adminJWT", token);
          }
        } catch (error) {
          console.error("Error al obtener token:", error);
          // Si hay error al obtener el token, no limpiar el usuario
          // ya que Firebase puede estar restaurando la sesión
        }
      } else {
        // Solo limpiar si realmente no hay usuario autenticado
        // Verificar que no sea un estado temporal durante la restauración
        setUser(null);
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        localStorage.removeItem("adminJWT");
      }
      // Marcar como cargado solo después de que Firebase haya verificado el estado
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const getToken = async () => {
    return await getFirebaseToken();
  };

  const value = {
    user,
    loading,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
}

